/* https://github.com/jgradim/react-user-media/blob/master/src/webcam/index.js

 The MIT License (MIT)

 Copyright (c) 2015 João Gradim

 Permission is hereby granted, free of charge, to any person obtaining a copy
 of this software and associated documentation files (the "Software"), to deal
 in the Software without restriction, including without limitation the rights
 to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 copies of the Software, and to permit persons to whom the Software is
 furnished to do so, subject to the following conditions:

 The above copyright notice and this permission notice shall be included in all
 copies or substantial portions of the Software.

 THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 SOFTWARE.

 This is also forked from https://github.com/notarize/react-user-media
 But could not add the repo as a dependency in package.json.
*/
import { Component } from "react";
import PropTypes from "prop-types";
// eslint-disable-next-line no-restricted-imports
import isEqual from "lodash/isEqual";

import {
  DESKTOP_VIDEO_HEIGHT,
  DESKTOP_VIDEO_WIDTH,
} from "common/video_conference/audio_video_settings/constraints";

class Webcam extends Component {
  static propTypes = {
    playsInline: PropTypes.bool,
    audio: PropTypes.bool,
    autoPlay: PropTypes.bool,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    captureFormat: PropTypes.oneOf(["image/png", "image/jpeg", "image/webp"]),
    onSuccess: PropTypes.func,
    onFailure: PropTypes.func,
    onVideoStart: PropTypes.func,
    label: PropTypes.string,
    className: PropTypes.string,
  };

  static defaultProps = {
    audio: true,
    autoPlay: true,
    video: {
      width: { ideal: DESKTOP_VIDEO_WIDTH },
      height: { ideal: DESKTOP_VIDEO_HEIGHT },
    },
    width: DESKTOP_VIDEO_WIDTH,
    height: DESKTOP_VIDEO_HEIGHT,
    captureFormat: "image/png",
    onSuccess: () => {},
    onFailure: (error) => {
      throw error;
    },
    onVideoStart: () => {},
  };

  mounted = false;
  hasGetUserMedia = false;
  streamRunning = false;
  currentUserMediaPromise = null;

  componentDidMount() {
    const { audio, video, onFailure } = this.props;
    this.hasGetUserMedia = Boolean(window.navigator.mediaDevices?.getUserMedia);
    this.mounted = true;
    this.requestUserMedia(audio, video, onFailure);
  }

  componentDidUpdate({ audio: oldAudio, video: oldVideo }) {
    // We need to check the audio and video props to know if we need to update
    const { video: newVideo, audio: newAudio, onFailure } = this.props;
    if (!isEqual(oldAudio, newAudio) || !isEqual(oldVideo, newVideo)) {
      this.requestUserMedia(newAudio, newVideo, onFailure);
    }
  }

  componentWillUnmount() {
    this.cleanUpMediaStream();
    this.mounted = false;
  }

  static _captureCanvas = null;

  setVideo = (elm) => {
    this.videoElement = elm;
  };

  cleanUpMediaStream() {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }
  }

  requestUserMedia(audio, video, onFailure) {
    if (!this.hasGetUserMedia) {
      return;
    }
    this.streamRunning = false;
    this.cleanUpMediaStream();
    const thisClosurePromise = (this.currentUserMediaPromise = navigator.mediaDevices
      .getUserMedia({ video, audio })
      .then((stream) => {
        if (!this.mounted || thisClosurePromise !== this.currentUserMediaPromise) {
          // If this is not the "latest" promise or we are unmounted, ignore.
          return;
        }
        const { videoElement } = this;
        const { onVideoStart, onSuccess } = this.props;
        videoElement.addEventListener("loadeddata", function videoStartCallback() {
          onVideoStart();
          videoElement.removeEventListener("loadeddata", videoStartCallback);
        });
        videoElement.srcObject = this.mediaStream = stream;
        videoElement.muted = true;

        this.streamRunning = true;
        onSuccess();
      })
      .catch((error) => {
        this.cleanUpMediaStream();
        onFailure(error);
      }));
  }

  _getCanvas(width, height) {
    if (!this._captureCanvas) {
      this._captureCanvas = document.createElement("canvas");
    }

    this._captureCanvas.width = width;
    this._captureCanvas.height = height;

    return this._captureCanvas;
  }

  // External methods

  captureCanvas(capWidth = this.props.width, capHeight = this.props.height) {
    if (this.streamRunning) {
      const canvas = this._getCanvas(capWidth, capHeight);
      const ctx = canvas.getContext("2d");

      ctx.drawImage(this.videoElement, 0, 0, capWidth, capHeight);

      return canvas;
    }
  }

  captureScreenshot(width, height) {
    const capWidth = width ? width : this.videoElement.videoWidth;
    const capHeight = height ? height : this.videoElement.videoHeight;
    const { captureFormat } = this.props;
    const canvas = this.captureCanvas(capWidth, capHeight);
    if (canvas) {
      return canvas.toDataURL(captureFormat);
    }
  }

  render() {
    const { width, height, playsInline, autoPlay, audio, label, className } = this.props;

    return (
      <video
        tabIndex={0}
        width={width}
        height={height}
        ref={this.setVideo}
        autoPlay={autoPlay}
        playsInline={playsInline}
        loop={playsInline}
        muted={!audio}
        data-automation-id={"video"}
        aria-label={label}
        className={className}
      />
    );
  }
}

export default Webcam;
